import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InputSearch from 'common/components/inputSearch/InputSearch';
import ListingTable, { ListingTableColumn } from 'common/components/listingTable/ListingTable';
import PaginationWithInfo from 'common/components/pagination/PaginationWithInfo';
import DateFormat from 'common/components/dateFormat/dateFormat';
import PrivacyService from 'api/privacy/PrivacyService';
import { DeletionHistoryDto } from 'api/privacy/models/DeletionHistoryDto';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE } from 'Config';
import styles from '../PrivacyTab.module.scss';

type Props = {
    refreshKey?: number;
    entityType?: 'driver' | 'vehicle';
};

const DeletionHistoryTable: React.FC<Props> = ({ refreshKey, entityType }: Props) => {
    const { t } = useTranslation();
    const [allItems, setAllItems] = useState<DeletionHistoryDto[]>([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    const loadHistory = useCallback(async () => {
        try {
            setLoading(true);
            const result = await PrivacyService.getDeletionHistory({ page: 1, itemsPerPage: 9999, allIn: search || undefined });
            const items = entityType ? result.items.filter(i => i.entityType?.toLowerCase() === entityType) : result.items;
            setAllItems(items);
            setCurrentPage(1);
        } catch {
            // Error handled by Http interceptor
        } finally {
            setLoading(false);
        }
    }, [search, refreshKey, entityType]);

    useEffect(() => {
        loadHistory();
    }, [loadHistory]);

    const totalItems = allItems.length;
    const pageItems = allItems.slice((currentPage - 1) * DEFAULT_PAGINATION_ITEMS_PER_PAGE, currentPage * DEFAULT_PAGINATION_ITEMS_PER_PAGE);

    const getStatusLabel = (status: string): string => {
        switch (status?.toLowerCase()) {
            case 'completed': return t('privacy.status_completed') as string;
            case 'failed': return t('privacy.status_failed') as string;
            case 'pending': return t('privacy.status_pending') as string;
            default: return status;
        }
    };

    const getStatusDotClass = (status: string): string => {
        switch (status?.toLowerCase()) {
            case 'completed': return styles.dotGreen;
            case 'failed': return styles.dotRed;
            case 'pending': return styles.dotYellow;
            default: return '';
        }
    };

    const titleKey = entityType === 'driver' ? 'privacy.anonymize_history_title' : 'privacy.deletion_history_title';
    const descKey = entityType === 'driver' ? 'privacy.anonymize_history_desc' : 'privacy.deletion_history_desc';

    const columns: ListingTableColumn<DeletionHistoryDto>[] = [
        ...(!entityType ? [{
            field: 'entityType' as const,
            name: t('privacy.deletion_col_type') as string,
            renderCell: (row: DeletionHistoryDto) => <>{row.entityType?.toLowerCase() === 'driver' ? (t('privacy.driver') as string) : (t('privacy.vehicle') as string)}</>,
        }] : []),
        {
            field: 'entityName',
            name: t('privacy.deletion_col_name') as string,
            renderCell: row => <><strong>{row.entityName}</strong></>,
        },
        {
            field: 'dateRangeStart',
            name: t('privacy.deletion_col_period') as string,
            renderCell: row => (
                <>
                    {row.dateRangeStart && row.dateRangeEnd
                        ? <><DateFormat value={row.dateRangeStart} format="DD/MM/YYYY | HH:mm" /> {' - '} <DateFormat value={row.dateRangeEnd} format="DD/MM/YYYY | HH:mm" /></>
                        : <>{t('privacy.all_data') as string}</>
                    }
                </>
            ),
        },
        {
            field: 'recordsDeleted',
            name: t('privacy.deletion_col_records') as string,
            renderCell: row => (
                <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                    {row.recordsDeleted != null ? row.recordsDeleted.toLocaleString() : '-'}
                </span>
            ),
        },
        {
            field: 'deletedByUserName',
            name: t('privacy.deletion_col_deleted_by') as string,
            renderCell: row => <>{row.deletedByUserName}</>,
        },
        {
            field: 'deletedDate',
            name: t('privacy.deletion_col_date') as string,
            renderCell: row => <><DateFormat value={row.deletedDate} format="DD/MM/YYYY | HH:mm" /></>,
        },
        {
            field: 'status',
            name: t('privacy.deletion_col_status') as string,
            renderCell: row => (
                <span className={styles.statusDot}>
                    <span className={`${styles.dot} ${getStatusDotClass(row.status)}`} />
                    {getStatusLabel(row.status)}
                </span>
            ),
        },
    ];

    return (
        <div className={styles.card}>
            <div className={styles.cardHeader}>
                <div>
                    <div className={styles.cardTitle}>{t(titleKey) as string}</div>
                    <div className={styles.cardDesc}>{t(descKey) as string}</div>
                </div>
                <InputSearch onChangeValue={val => setSearch(val)} placeholder={t('privacy.search') as string} />
            </div>
            <div className={styles.cardBodyNoPadding}>
                <ListingTable
                    columns={columns}
                    rows={pageItems}
                    allowHover
                />
                <PaginationWithInfo
                    itemName=""
                    currentPage={currentPage}
                    pageItems={pageItems.length}
                    totalItems={totalItems}
                    onChange={setCurrentPage}
                />
            </div>
        </div>
    );
};

export default DeletionHistoryTable;
